import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = not logged in, object = company
    const [company, setCompany] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setCompany(data);
            return data;
        } catch {
            setCompany(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (company_name, password) => {
        try {
            const { data } = await api.post("/auth/login", { company_name, password });
            // Three possible response shapes:
            //  - { company }                 → logged in immediately
            //  - { requires_totp, login_token } → caller must complete via /auth/login/totp
            //  - { requires_totp_setup, setup_token } → caller must enrol first
            if (data.requires_totp || data.requires_totp_setup) {
                return {
                    ok: true,
                    requires_totp: !!data.requires_totp,
                    requires_totp_setup: !!data.requires_totp_setup,
                    login_token: data.login_token,
                    setup_token: data.setup_token,
                };
            }
            setCompany(data.company);
            return { ok: true, company: data.company, isAdmin: data.company?.role === "admin" };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    /** Complete a TOTP challenge with either `code` (6-digit) or `backup_code`. */
    const loginTotp = async ({ login_token, code, backup_code }) => {
        try {
            const { data } = await api.post("/auth/login/totp", { login_token, code, backup_code });
            setCompany(data.company);
            return { ok: true, company: data.company, used_backup_code: !!data.used_backup_code };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            if (data.requires_totp_setup) {
                return { ok: true, requires_totp_setup: true, setup_token: data.setup_token };
            }
            if (data.company) setCompany(data.company);
            return { ok: true, company: data.company };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* ignore */
        }
        setCompany(false);
    };

    return (
        <AuthContext.Provider value={{ company, login, loginTotp, register, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
